/**
 * Fields Query Hooks
 * React Query hooks for fields API with mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getFields,
    getFieldsForCollection,
    getField,
    createField,
    updateField,
    deleteField,
    type Field,
    type CreateFieldPayload,
    type UpdateFieldPayload,
} from '@/api/fields';
import { collectionsKeys } from './useCollections';
import { toast } from 'sonner';

// Query keys
export const fieldsKeys = {
    all: ['fields'] as const,
    list: () => [...fieldsKeys.all, 'list'] as const,
    collection: (collection: string) => [...fieldsKeys.all, 'collection', collection] as const,
    detail: (collection: string, field: string) => [...fieldsKeys.all, 'detail', collection, field] as const,
};

// ============== Queries ==============

export function useFieldsQuery() {
    return useQuery({
        queryKey: fieldsKeys.list(),
        queryFn: () => getFields(),
    });
}

export function useFieldsForCollectionQuery(collection: string) {
    return useQuery({
        queryKey: fieldsKeys.collection(collection),
        queryFn: () => getFieldsForCollection(collection),
        enabled: !!collection,
    });
}

export function useFieldQuery(collection: string, field: string) {
    return useQuery({
        queryKey: fieldsKeys.detail(collection, field),
        queryFn: () => getField(collection, field),
        enabled: !!collection && !!field,
    });
}

// ============== Mutations ==============

export function useCreateField(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: CreateFieldPayload) => createField(collection, payload),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: fieldsKeys.list() });
            queryClient.invalidateQueries({ queryKey: fieldsKeys.collection(collection) });
            queryClient.invalidateQueries({ queryKey: collectionsKeys.detail(collection) });
            toast.success(`Field "${data.field}" created`);
        },
        onError: (error: Error) => {
            toast.error(`Failed to create field: ${error.message}`);
        },
    });
}

// Aliases for cleaner API
export const useCreateFieldMutation = useCreateField;
export const useFields = useFieldsForCollectionQuery;
export const useField = useFieldQuery;
export const useUpdateField = useUpdateFieldMutation;
export const useDeleteField = useDeleteFieldMutation;

export function useUpdateFieldMutation(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ field, payload }: { field: string; payload: UpdateFieldPayload }) =>
            updateField(collection, field, payload),
        onSuccess: (_, { field }) => {
            queryClient.invalidateQueries({ queryKey: fieldsKeys.list() });
            queryClient.invalidateQueries({ queryKey: fieldsKeys.collection(collection) });
            queryClient.invalidateQueries({ queryKey: fieldsKeys.detail(collection, field) });
            queryClient.invalidateQueries({ queryKey: collectionsKeys.detail(collection) });
        },
        onError: (error: Error) => {
            toast.error(`Failed to update field: ${error.message}`);
        },
    });
}

export function useDeleteFieldMutation(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (field: string) => deleteField(collection, field),
        onSuccess: (_, field) => {
            queryClient.invalidateQueries({ queryKey: fieldsKeys.list() });
            queryClient.invalidateQueries({ queryKey: fieldsKeys.collection(collection) });
            queryClient.removeQueries({ queryKey: fieldsKeys.detail(collection, field) });
            queryClient.invalidateQueries({ queryKey: collectionsKeys.detail(collection) });
            toast.success('Field deleted');
        },
        onError: (error: Error) => {
            toast.error(`Failed to delete field: ${error.message}`);
        },
    });
}

// ============== Utilities ==============

/**
 * Get field type icon name
 */
export function getFieldTypeIcon(type: string): string {
    const iconMap: Record<string, string> = {
        // String types
        string: 'text',
        text: 'text',
        varchar: 'text',
        char: 'text',

        // Number types
        integer: 'number',
        bigint: 'number',
        float: 'number',
        decimal: 'number',
        numeric: 'number',

        // Boolean
        boolean: 'toggle',

        // Date/Time
        timestamp: 'calendar',
        date: 'calendar',
        time: 'clock',
        datetime: 'calendar',

        // JSON
        json: 'braces',
        jsonb: 'braces',

        // UUID
        uuid: 'key',

        // Binary
        binary: 'binary',
        bytea: 'binary',

        // Alias types (CMS virtual)
        alias: 'square-asterisk',
    };

    return iconMap[type.toLowerCase()] || 'type';
}

/**
 * Sort fields by sort order, then alphabetically
 */
export function sortFieldsByOrder(fields: Field[]) {
    return [...fields].sort((a, b) => {
        // Sort by meta.sort first
        const aSort = a.meta?.sort ?? Infinity;
        const bSort = b.meta?.sort ?? Infinity;

        if (aSort !== bSort) return aSort - bSort;

        // Then alphabetically
        return a.field.localeCompare(b.field);
    });
}

/**
 * Check if field is a primary key
 */
export function isPrimaryKey(field: Field): boolean {
    return field.schema?.is_primary_key === true;
}

/**
 * Check if field is hidden
 */
export function isHiddenField(field: Field): boolean {
    return field.meta?.hidden === true;
}

/**
 * Check if field is system managed
 */
export function isSystemManagedField(field: Field): boolean {
    return field.meta?.system === true;
}
